import { memo, useContext, useEffect, useState } from 'react';
import AnimateHeight from 'react-animate-height';

import { BucketContext, BudgetContext, S3Item, useBudgetProduct } from 'contexts';

import { Button } from 'components/Button';
import { Table } from 'components/Table';

import * as S from './BudgetProduct.styles';
import { useColumns } from './columns.data';
import { SubProduct } from './components/SubProduct';

type BudgetProductProps = {
  productId: string;
};

function BudgetProductRow({ productId }: BudgetProductProps): React.ReactElement {
  const [isSubListVisible, isSubListVisibleSet] = useState(false);
  // Colour rows (a table + an image each) are mounted on the first expand only,
  // then kept so collapsing still animates.
  const [wasExpanded, wasExpandedSet] = useState(false);
  const [image, imageSet] = useState<string>('');

  const { getUrl } = useContext(BucketContext);
  const { date } = useContext(BudgetContext);

  // Subscribes to this product only: saving another product doesn't re-render this one
  const product = useBudgetProduct(productId);

  const mainProduct = product?.mainProduct;
  const colors = product?.colors;

  const columns = useColumns(product, date);

  const childrenMapped = colors?.map((item) => (
    <SubProduct productId={productId} colorId={item.id} key={item.id} />
  ));

  useEffect(() => {
    if (!mainProduct?.imageName) return;

    getUrl(`catalogue/products${mainProduct.imageName}`).then((res: S3Item) => {
      imageSet(res.url);
    });
  }, [mainProduct?.imageName, getUrl]);

  return (
    <>
      <S.Container>
        {colors && colors.length > 0 ? (
          <Button
            data-testid="plus-button"
            iconLeft={isSubListVisible ? 'FiMinus' : 'FiPlus'}
            onClick={() => {
              isSubListVisibleSet(!isSubListVisible);
              wasExpandedSet(true);
            }}
          />
        ) : undefined}

        <S.ImageContainer>
          <S.Img source={image} alt={mainProduct?.itemCode || ''} isBudget={true} />
        </S.ImageContainer>

        <Table motive="budget" columns={columns} data={mainProduct ? [mainProduct] : []} />
      </S.Container>

      {colors && colors.length > 0 ? (
        <AnimateHeight
          data-testid="sub-item-container"
          duration={500}
          height={isSubListVisible ? 'auto' : 0}
        >
          {wasExpanded ? childrenMapped : undefined}
        </AnimateHeight>
      ) : undefined}
    </>
  );
}

export const BudgetProduct = memo(BudgetProductRow);
